import { apiBaseUrl } from '../api/baseUrl';
import { noteIfGateway, noteRestoreUnreachable } from './restoreReachability';

// ─────────────────────────────────────────────────────────────────────
// THE MEMBER SESSION (#185).
//
// The sibling of clubSession: same shape, different subject. A club
// administrator signs in with an account; a player signs in with the club's
// JOIN CODE and a claim about which member they are.
//
// WHAT THIS PROVES, AND WHAT IT DOES NOT.
//
// The code proves the caller was handed something by that club. The member
// then picks their own name and nothing verifies the choice, so the server
// issues the session at `SELF_ASSERTED` and says so in the response. That value
// is carried here rather than discarded, because a screen deciding what to
// offer should read how weak the claim is instead of inferring it from the fact
// that a session exists at all.
//
// It is a deliberate, temporary posture for the cutover — see
// apps/api/src/routes/memberAuth.ts. Real member accounts are #316. What it
// buys over the alternative (an unauthenticated public data path) is that every
// action is attributable to a session the server can name and revoke, and the
// whole thing upgrades to VERIFIED without a single guard changing.
//
// THE TOKEN LIVES IN MEMORY, for the same reasons as the club one: web storage
// is readable by any script that reaches the page, and outlives the tab that
// earned it. That has not changed and must not.
//
// WHAT CHANGED IS THAT «SIGNED IN» NO LONGER DEPENDS ON IT (#620). This comment
// used to end «a reload returns to the entry screen, which is the intended
// behaviour rather than a gap to paper over» — the club session said the same
// thing and stopped saying it at #569, for the same reason. Here the cost was
// larger than a login screen: the member app IS the reload-prone surface. A deep
// link, a shared URL, the OS reloading the WebView, or a card method that
// redirects off the page all ended the session, and the one that found it was a
// member thrown back to the join screen holding an unpaid booking.
//
// So the API now also issues the SAME session as an `HttpOnly; Secure;
// SameSite=Lax` cookie under its own name (`spm_session`), which this code cannot
// read and does not try to: `restore()` asks `/member/me` on boot and the server
// answers who it is. Two shapes of a live session, and the difference matters:
//
//   just signed in    `token` is set. The transport sends it as a bearer
//                     header, which the server prefers whenever it is present.
//   after a reload    `token` is null and the cookie authenticates. Every
//                     request carries `credentials: 'include'` already, and the
//                     server falls back to the cookie only when no header came.
//
// Nothing here may reintroduce a readable copy of the token to collapse those two
// into one. See `apps/api/src/auth/sessionCookie.ts` for the other half.
// ─────────────────────────────────────────────────────────────────────

/** How well the server believes the caller is who they claim. */
export type MemberAssurance = 'SELF_ASSERTED' | 'VERIFIED' | string;

export interface MemberIdentity {
  id: string;
  name: string | null;
}

export interface MemberClub {
  /**
   * The organization's ID, or null when the server did not say (#750 M2b-2).
   *
   * NEEDED TO ADDRESS THE CLUB. `X-Starpoint-Tenant` carries an id, so a
   * session holding only the slug cannot name the club it is for — which is
   * what an account-derived member context has to do. Nullable because a
   * session restored from a server that predates this answers without one, and
   * a null address is better than another club's.
   */
  id: string | null;
  slug: string;
  name: string | null;
}

export type MemberSessionStatus = 'signed-out' | 'signed-in';

export interface MemberSessionState {
  status: MemberSessionStatus;
  member: MemberIdentity | null;
  club: MemberClub | null;
  /**
   * `SELF_ASSERTED` today. Exposed so a screen can reason about what to offer
   * rather than assume a session means a verified person.
   */
  assurance: MemberAssurance | null;
  /** Why it ended, when it ended on its own. */
  endedBecause: 'unauthorized' | 'signed-out' | null;
}

export interface MemberSession {
  state(): MemberSessionState;
  token(): string | null;
  // NO JOIN-CODE SIGN-IN EITHER (#750). `signIn` drove `/auth/member/start`,
  // which took a club's join code and a self-chosen name. Its one caller,
  // `MemberJoinScreen`, was not rendered after the #710 direction correction,
  // and the door, the screen and this method are retired together.
  // NO E-MAIL CODE HERE ANY MORE (#750). `requestCode` / `verifyCode` drove the
  // legacy `/auth/member/otp` door, which issued an `spm_` session for a
  // mailed code. Their one caller, `MemberSignInScreen`, had not been rendered
  // since the #710 direction correction, and the door and the screen are
  // retired with them. A player proves their address at `/auth/otp`
  // (`lib/api/accountSignIn.ts`), which issues an ACCOUNT session — held by
  // `clubSession`, not by this.
  /**
   * Take over a member session the API issued through another door (the
   * dev-persona route). A real token, from the same issuer as every `spm_`.
   */
  adopt(session: {
    token: string;
    expiresAt: string | null;
    assurance: string | null;
    member: MemberIdentity;
    club: MemberClub;
  }): void;
  /**
   * Re-establish a session from the cookie, on boot (#620).
   *
   * The token lived only in memory, so ANY full page load signed the member out
   * and the app asked for a club code again — a refresh, a deep link, a shared
   * URL, the OS reloading the WebView, or a payment method that redirects. The API
   * now also delivers the session as an `HttpOnly` cookie, which this code cannot
   * read and does not try to: it asks `/member/me` and the server answers who it
   * is, exactly as the club session does.
   *
   * Resolves `true` when a session was restored. A `false` is NOT «you were
   * signed out» — it is «there was nothing here», which is the ordinary state for
   * a first visit, so it sets no `endedBecause`.
   */
  restore(): Promise<boolean>;
  signOut(): Promise<void>;
  /** Drop the session because the API refused it. Sends nothing. */
  invalidate(): void;
  subscribe(listener: (state: MemberSessionState) => void): () => void;
}

export interface MemberSessionDeps {
  baseUrl: () => string;
  fetchImpl: typeof fetch;
  now: () => number;
}

const SIGNED_OUT: MemberSessionState = {
  status: 'signed-out',
  member: null,
  club: null,
  assurance: null,
  endedBecause: null,
};

export function createMemberSession(deps: Partial<MemberSessionDeps> = {}): MemberSession {
  const baseUrl = deps.baseUrl ?? apiBaseUrl;
  const fetchImpl = deps.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const now = deps.now ?? (() => Date.now());

  let token: string | null = null;
  let expiresAt: number | null = null;
  let state: MemberSessionState = SIGNED_OUT;
  const listeners = new Set<(s: MemberSessionState) => void>();

  const set = (next: MemberSessionState) => {
    state = next;
    for (const l of [...listeners]) l(next);
  };

  const drop = (endedBecause: MemberSessionState['endedBecause']) => {
    token = null;
    expiresAt = null;
    set({ ...SIGNED_OUT, endedBecause });
  };

  return {
    state: () => state,

    async restore() {
      const base = baseUrl();
      if (!base) return false;
      let res: Response;
      try {
        res = await fetchImpl(base + '/member/me', { credentials: 'include' });
      } catch {
        // NO ANSWER is not «no session» (#1145): the gate shows «no connection».
        noteRestoreUnreachable();
        return false;
      }
      if (!res.ok) {
        noteIfGateway(res.status);
        return false;
      }
      const body = (await res.json().catch(() => null)) as {
        member?: { id?: unknown; name?: unknown };
        club?: { id?: unknown; slug?: unknown; name?: unknown };
        assurance?: unknown;
      } | null;
      const id = body?.member?.id;
      if (typeof id !== 'string' || id === '') return false;

      // THE TOKEN STAYS NULL. There is no readable copy anywhere — the cookie is
      // what authenticates from here until the member signs in again — and
      // `apiFetch` already sends credentials, so requests work without one.
      token = null;
      expiresAt = null;
      set({
        status: 'signed-in',
        member: { id, name: body?.member?.name == null ? null : String(body.member.name) },
        club: {
          // `/member/me` has always answered with the id; it was simply
          // discarded until the context needed it (#750 M2b-2).
          id: typeof body?.club?.id === 'string' && body.club.id ? body.club.id : null,
          slug: body?.club?.slug == null ? '' : String(body.club.slug),
          name: body?.club?.name == null ? null : String(body.club.name),
        },
        assurance: body?.assurance == null ? null : String(body.assurance),
        endedBecause: null,
      });
      return true;
    },

    token() {
      if (!token) return null;
      // An expiry the client can already see is worth honouring: sending a
      // token known to be dead buys a guaranteed 401 and a round trip. The
      // SERVER remains the authority; this only declines to ask a question
      // whose answer is known.
      if (expiresAt != null && now() >= expiresAt) {
        drop('unauthorized');
        return null;
      }
      return token;
    },

    adopt(session) {
      token = session.token;
      const parsed = typeof session.expiresAt === 'string' ? Date.parse(session.expiresAt) : NaN;
      expiresAt = Number.isFinite(parsed) ? parsed : null;
      set({
        status: 'signed-in',
        member: session.member,
        club: session.club,
        assurance: session.assurance,
        endedBecause: null,
      });
    },

    async signOut() {
      const base = baseUrl();
      const current = token;
      // Local state is cleared FIRST and unconditionally: a sign-out that fails
      // on the network must still end signed out here.
      drop('signed-out');
      // ATTEMPTED WHENEVER THERE IS AN API, not only when this tab still holds a
      // token (#620). After a reload the token is null and the cookie is the only
      // credential — so `if (!current) return` skipped the call entirely, leaving
      // the database session live for the rest of its thirty days and the cookie in
      // the browser. The next reload then signed the member straight back in. A
      // logout that clears the screen and nothing else is the worst of the three
      // outcomes, and it is the one a RESTORED session got.
      if (!base) return;
      try {
        await fetchImpl(base + '/auth/member/logout', {
          method: 'POST',
          credentials: 'include',
          // No empty bearer when there is no token: `Bearer ` with nothing after it
          // is a PRESENT, invalid header, and the server reads the cookie only when
          // no header arrived at all.
          ...(current ? { headers: { Authorization: `Bearer ${current}` } } : {}),
        });
      } catch {
        /* the server's copy outliving ours is the server's to expire */
      }
    },

    invalidate() {
      if (!token && state.status === 'signed-out') return;
      drop('unauthorized');
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
